import { useState, useEffect, useRef, useMemo, lazy, Suspense, Fragment, useEffectEvent } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  createProfile,
  listProfiles,
  getProfile,
  updateProfile,
  deleteProfile,
  uploadTestLibrary,
  startAnalysis,
  getAnalysisStatus,
  getAnalysisResults,
  exportAnalysis,
  getJiraStatus,
  getJiraBoards,
  searchJiraByJql,
  updateAutoRun,
  getAutoRun,
  type ProfileSummary,
  type AppProfile,
  type RiskAnalysisResults,
  type RiskWeights,
  type JiraConnectionInfo,
  type JiraSprintIssue,
  type AutoRunConfig,
} from '../api'
const RiskPrioritizationInstructions = lazy(() => import('./RiskPrioritizationInstructions'))
import AppLayout from '../components/AppLayout'
import SprintImportModal from '../components/SprintImportModal'
import { friendlyError, errorMessage } from '../utils/friendlyError'
import { formatPercent } from '../utils/formatPercent'
import { redistributeWeights } from '../utils/redistributeWeights'
import { linkifyJiraKeys, resolveJiraBaseUrl } from '../utils/linkifyJiraKeys'

type Screen = 'profiles' | 'setup' | 'analysis' | 'results' | 'instructions'
type SetupStep = 'upload' | 'context' | 'weights' | 'review'

const EXCLUDED_TICKET_STATUSES = ['done', "won't do", "won't fix", 'cancelled', 'canceled', 'closed', 'rejected', 'resolved', 'complete']

export default function RiskPrioritization() {
  const navigate = useNavigate()
  const location = useLocation()

  // Profile state
  const [profiles, setProfiles] = useState<ProfileSummary[]>([])
  const [currentProfile, setCurrentProfile] = useState<AppProfile | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [warning, setWarning] = useState<string | null>(null)

  // Setup wizard state
  const [setupStep, setSetupStep] = useState<SetupStep>('upload')
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [dragActive, setDragActive] = useState(false)
  const [newProfileName, setNewProfileName] = useState('')
  const [uploadMode, setUploadMode] = useState<'replace' | 'append'>('replace')

  // Analysis state
  const [jiraTickets, setJiraTickets] = useState<string[]>([])
  const [currentTicketInput, setCurrentTicketInput] = useState('')
  const [analysisJobId, setAnalysisJobId] = useState<string | null>(null)
  const [analysisStatus, setAnalysisStatus] = useState<'idle' | 'processing' | 'completed' | 'failed'>('idle')
  const [analysisResults, setAnalysisResults] = useState<RiskAnalysisResults | null>(null)

  // JIRA connection state
  const [jiraStatus, setJiraStatus] = useState<JiraConnectionInfo | null>(null)
  const jiraBaseUrl = useMemo(() => resolveJiraBaseUrl(jiraStatus), [jiraStatus])

  // Sprint import modal state
  const [sprintModalOpen, setSprintModalOpen] = useState(false)

  // JQL import state
  const [jqlExpanded, setJqlExpanded] = useState(false)
  const [jqlQuery, setJqlQuery] = useState('')
  const [jqlResults, setJqlResults] = useState<JiraSprintIssue[]>([])
  const [jqlTotal, setJqlTotal] = useState(0)
  const [jqlHasMore, setJqlHasMore] = useState(false)
  const [jqlLoading, setJqlLoading] = useState(false)
  const [jqlError, setJqlError] = useState<string | null>(null)
  const [jqlSelectedKeys, setJqlSelectedKeys] = useState<Set<string>>(new Set())

  // Auto-run state
  const [autoRunConfig, setAutoRunConfig] = useState<AutoRunConfig | null>(null)
  const [autoRunSaving, setAutoRunSaving] = useState(false)
  const [autoRunTooltipVisible, setAutoRunTooltipVisible] = useState(false)
  const [noJiraBoards, setNoJiraBoards] = useState(false)

  // UI state
  const [currentScreen, setCurrentScreen] = useState<Screen>('profiles')
  const [expandedAreas, setExpandedAreas] = useState<Set<string>>(new Set())
  const [expandedAreaTestCases, setExpandedAreaTestCases] = useState<Set<string>>(new Set())
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false)
  const [initializing, setInitializing] = useState(true)
  const [deleteModalProfileId, setDeleteModalProfileId] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const isInitialMount = useRef(true)

  // Fetch JIRA connection status on mount
  useEffect(() => {
    getJiraStatus()
      .then(setJiraStatus)
      .catch(() => setJiraStatus(null))
  }, [])

  // Fetch auto-run config when profile changes
  useEffect(() => {
    if (currentProfile?.profileId) {
      getAutoRun(currentProfile.profileId)
        .then(data => setAutoRunConfig(data.autoRun))
        .catch(() => setAutoRunConfig(null))
    }
  }, [currentProfile?.profileId])

  // Check if JIRA has any boards — used to disable auto-run toggle proactively.
  // Re-runs when JIRA status changes or user navigates back to the analysis screen.
  useEffect(() => {
    if (jiraStatus?.status === 'active' && currentScreen === 'analysis') {
      getJiraBoards()
        .then(({ boards }) => setNoJiraBoards(boards.length === 0))
        .catch(() => setNoJiraBoards(false))
    } else {
      setNoJiraBoards(false)
    }
  }, [jiraStatus?.status, currentScreen, currentProfile?.profileId])

  // Parse URL and load initial state on mount and location changes
  useEffect(() => {
    const path = location.pathname
    isInitialMount.current = true

    // Match: /risk-prioritization/{profileId}/{analysisId}
    const analysisMatch = path.match(/^\/risk-prioritization\/(profile_[^/]+)\/(analysis_[^/]+)$/)
    if (analysisMatch) {
      const [, profileId, analysisId] = analysisMatch
      loadProfileAndAnalysis(profileId, analysisId)
      return
    }

    // Match: /risk-prioritization/{profileId}/setup
    const setupMatch = path.match(/^\/risk-prioritization\/(profile_[^/]+)\/setup$/)
    if (setupMatch) {
      const [, profileId] = setupMatch
      loadProfileForSetup(profileId)
      return
    }

    // Match: /risk-prioritization/{profileId}
    const profileMatch = path.match(/^\/risk-prioritization\/(profile_[^/]+)$/)
    if (profileMatch) {
      const [, profileId] = profileMatch
      loadProfileForAnalysis(profileId)
      return
    }

    // Match: /risk-prioritization/instructions
    if (path.includes('/instructions')) {
      setCurrentScreen('instructions')
      loadProfiles()
      return
    }

    // Default: load profiles list and reset to profiles screen
    setCurrentScreen('profiles')
    loadProfiles()
  }, [location.pathname])

  // Poll analysis status
  // navigate changes identity on every route change under BrowserRouter;
  // reading it through an effect event keeps it from restarting the poll.
  const goToResults = useEffectEvent((path: string) => navigate(path))
  useEffect(() => {
    if (analysisJobId && analysisStatus === 'processing' && currentProfile) {
      const interval = setInterval(async () => {
        try {
          const status = await getAnalysisStatus(analysisJobId)
          if (status.status === 'completed') {
            setAnalysisStatus('completed')
            const results = await getAnalysisResults(analysisJobId)
            setAnalysisResults(results)
            setCurrentScreen('results')
            goToResults(`/risk-prioritization/${currentProfile.profileId}/${analysisJobId}`)
            clearInterval(interval)
          } else if (status.status === 'failed') {
            setAnalysisStatus('failed')
            setError(status.error || 'Analysis failed')
            clearInterval(interval)
          }
        } catch (err) {
          console.error('Error polling analysis:', err)
        }
      }, 2000)
      return () => clearInterval(interval)
    }
  }, [analysisJobId, analysisStatus, currentProfile])

  async function loadProfiles() {
    setLoading(true)
    setError(null)
    try {
      const data = await listProfiles()
      setProfiles(data.profiles)
    } catch (err) {
      setError(friendlyError(errorMessage(err) || 'Failed to load profiles', 'load your profiles'))
    } finally {
      setLoading(false)
      setInitializing(false)
      isInitialMount.current = false
    }
  }

  async function loadProfileForAnalysis(profileId: string) {
    setCurrentScreen('analysis')
    setLoading(true)
    setError(null)
    try {
      const [profilesData, profile] = await Promise.all([
        listProfiles(),
        getProfile(profileId)
      ])
      setProfiles(profilesData.profiles)
      setCurrentProfile(profile)
    } catch (err) {
      setError(friendlyError(errorMessage(err) || 'Failed to load profile', 'load this profile'))
      setCurrentScreen('profiles')
    } finally {
      setLoading(false)
      setInitializing(false)
      isInitialMount.current = false
    }
  }

  async function loadProfileForSetup(profileId: string) {
    setCurrentScreen('setup')
    setSetupStep('upload')
    setLoading(true)
    setError(null)
    try {
      const [profilesData, profile] = await Promise.all([
        listProfiles(),
        getProfile(profileId)
      ])
      setProfiles(profilesData.profiles)
      setCurrentProfile(profile)
    } catch (err) {
      setError(friendlyError(errorMessage(err) || 'Failed to load profile', 'load this profile'))
      setCurrentScreen('profiles')
    } finally {
      setLoading(false)
      setInitializing(false)
      isInitialMount.current = false
    }
  }

  async function loadProfileAndAnalysis(profileId: string, analysisId: string) {
    setCurrentScreen('results')
    setLoading(true)
    setError(null)
    try {
      const [profilesData, profile, results] = await Promise.all([
        listProfiles(),
        getProfile(profileId),
        getAnalysisResults(analysisId)
      ])
      setProfiles(profilesData.profiles)
      setCurrentProfile(profile)
      setAnalysisResults(results)
      setAnalysisJobId(analysisId)
      setAnalysisStatus('completed')
    } catch (err) {
      setError(friendlyError(errorMessage(err) || 'Failed to load analysis', 'load the analysis results'))
      setCurrentScreen('profiles')
    } finally {
      setLoading(false)
      setInitializing(false)
      isInitialMount.current = false
    }
  }

  async function handleCreateProfile() {
    if (!newProfileName.trim()) {
      setError('Profile name is required')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const result = await createProfile(newProfileName.trim())
      const profile = await getProfile(result.profileId)
      setCurrentProfile(profile)
      setNewProfileName('')
      setCurrentScreen('setup')
      setSetupStep('upload')
      navigate(`/risk-prioritization/${result.profileId}/setup`)
    } catch (err) {
      setError(friendlyError(errorMessage(err) || 'Failed to create profile', 'create the profile'))
    } finally {
      setLoading(false)
    }
  }

  async function handleSelectProfile(profileId: string) {
    setError(null)
    try {
      const profile = await getProfile(profileId)
      setCurrentProfile(profile)
      if (profile.testLibrary.totalTestCases === 0) {
        setCurrentScreen('setup')
        setSetupStep('upload')
        navigate(`/risk-prioritization/${profileId}/setup`)
      } else {
        setCurrentScreen('analysis')
        navigate(`/risk-prioritization/${profileId}`)
      }
    } catch (err) {
      setError(friendlyError(errorMessage(err) || 'Failed to load profile', 'load this profile'))
    } finally {
      setLoading(false)
    }
  }

  async function handleDeleteProfile(profileId: string) {
    setIsDeleting(true)
    setError(null)
    try {
      await deleteProfile(profileId)
      await loadProfiles()
      setDeleteModalProfileId(null)
    } catch (err) {
      setError(friendlyError(errorMessage(err) || 'Failed to delete profile', 'delete the profile'))
    } finally {
      setIsDeleting(false)
    }
  }

  async function handleUploadCSV() {
    if (!csvFile || !currentProfile) return
    setLoading(true)
    setError(null)
    setWarning(null)
    try {
      const reader = new FileReader()
      reader.onload = async () => {
        try {
          const base64 = btoa(reader.result as string)
          const result = await uploadTestLibrary(currentProfile.profileId, csvFile, base64, uploadMode)
          setCurrentProfile({ ...currentProfile, testLibrary: result.testLibrary })

          // Show success message with details
          if (uploadMode === 'append') {
            alert(`Successfully added ${result.added} test cases. Total: ${result.total} test cases`)
          }

          // Show warning if mixed formats detected
          if (result.warning) {
            setWarning(result.warning)
          }

          setLoading(false)
          setSetupStep('context')
        } catch (err) {
          setError(friendlyError(errorMessage(err) || 'Failed to upload CSV', 'upload the CSV file'))
          setLoading(false)
        }
      }
      reader.onerror = () => {
        setError('Failed to read file')
        setLoading(false)
      }
      reader.readAsBinaryString(csvFile)
    } catch (err) {
      setError(friendlyError(errorMessage(err) || 'Failed to process file', 'process the file'))
      setLoading(false)
    }
  }

  async function handleSaveContexts() {
    if (!currentProfile) return
    setLoading(true)
    setError(null)
    try {
      await updateProfile(currentProfile.profileId, {
        areaContexts: currentProfile.areaContexts,
      })
      setSetupStep('weights')
    } catch (err) {
      setError(friendlyError(errorMessage(err) || 'Failed to save contexts', 'save your area contexts'))
    } finally {
      setLoading(false)
    }
  }

  async function handleSaveWeights() {
    if (!currentProfile) return
    setLoading(true)
    setError(null)
    try {
      await updateProfile(currentProfile.profileId, {
        riskWeights: currentProfile.riskWeights,
      })
      setSetupStep('review')
    } catch (err) {
      setError(friendlyError(errorMessage(err) || 'Failed to save weights', 'save your risk weights'))
    } finally {
      setLoading(false)
    }
  }

  async function handleCompleteSetup() {
    if (currentProfile) {
      setCurrentScreen('analysis')
      setSetupStep('upload')
      navigate(`/risk-prioritization/${currentProfile.profileId}`)
    }
  }

  async function handleStartAnalysis() {
    if (!currentProfile || jiraTickets.length === 0) {
      setError('JIRA tickets are required')
      return
    }

    setLoading(true)
    setError(null)
    setWarning(null)
    setAnalysisStatus('processing')
    try {
      const result = await startAnalysis({
        profileId: currentProfile.profileId,
        jiraTickets: jiraTickets,
      })
      setAnalysisJobId(result.analysisId)
    } catch (err) {
      setError(friendlyError(errorMessage(err) || 'Failed to start analysis', 'start the analysis'))
      setAnalysisStatus('failed')
    } finally {
      setLoading(false)
    }
  }

  function addJiraTicket(ticket: string) {
    const trimmed = ticket.trim().toUpperCase()
    // Basic JIRA ticket format validation
    if (trimmed && /^[A-Z]+-\d+$/.test(trimmed) && !jiraTickets.includes(trimmed)) {
      setJiraTickets([...jiraTickets, trimmed])
      setCurrentTicketInput('')
    } else if (trimmed && !/^[A-Z]+-\d+$/.test(trimmed)) {
      setError('Invalid ticket format. Use format like BAT-100')
      setTimeout(() => setError(null), 3000)
    }
  }

  function removeJiraTicket(ticket: string) {
    setJiraTickets(jiraTickets.filter((t) => t !== ticket))
  }

  function handleTicketInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (currentTicketInput.trim()) {
        addJiraTicket(currentTicketInput)
      } else if (jiraTickets.length > 0) {
        // If input is empty and we have tickets, trigger analysis
        handleStartAnalysis()
      }
    } else if (e.key === ',' || e.key === ' ') {
      e.preventDefault()
      if (currentTicketInput.trim()) {
        addJiraTicket(currentTicketInput)
      }
    } else if (e.key === 'Backspace' && currentTicketInput === '' && jiraTickets.length > 0) {
      // Remove last ticket when backspace on empty input
      setJiraTickets(jiraTickets.slice(0, -1))
    }
  }

  async function handleExport(format: 'csv' | 'json') {
    if (!analysisJobId) return
    try {
      const blob = await exportAnalysis(analysisJobId, format)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `risk-analysis-${analysisJobId}.${format}`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setError(friendlyError(errorMessage(err) || 'Failed to export', 'export the results'))
    }
  }

  function toggleArea(areaId: string) {
    const newSet = new Set(expandedAreas)
    if (newSet.has(areaId)) {
      newSet.delete(areaId)
    } else {
      newSet.add(areaId)
    }
    setExpandedAreas(newSet)
  }

  function toggleAreaTestCases(areaId: string) {
    const newSet = new Set(expandedAreaTestCases)
    if (newSet.has(areaId)) {
      newSet.delete(areaId)
    } else {
      newSet.add(areaId)
    }
    setExpandedAreaTestCases(newSet)
  }

  function adjustWeight(key: keyof RiskWeights, delta: number) {
    if (!currentProfile) return
    const newValue = currentProfile.riskWeights[key] + delta
    const nextWeights = redistributeWeights(currentProfile.riskWeights, key, newValue)
    setCurrentProfile({ ...currentProfile, riskWeights: nextWeights })
  }

  // Status color helper for JIRA issue badges
  const statusColor = (color: string) => {
    switch (color) {
      case 'blue-gray': return 'bg-gray-500/20 text-gray-400'
      case 'blue': return 'bg-info/15 text-info'
      case 'green': return 'bg-success/15 text-success'
      case 'yellow': return 'bg-warning/15 text-warning'
      default: return 'bg-gray-500/20 text-gray-400'
    }
  }

  // JQL handlers
  const handleJqlPreview = async () => {
    setJqlLoading(true)
    setJqlError(null)
    setJqlSelectedKeys(new Set())
    try {
      const result = await searchJiraByJql(jqlQuery.trim())
      if (result.error) {
        setJqlError(result.error)
        setJqlResults([])
        setJqlTotal(0)
        setJqlHasMore(false)
      } else {
        setJqlResults(result.issues)
        setJqlTotal(result.total)
        setJqlHasMore(result.hasMore)
        const activeJqlIssues = result.issues.filter(i => !EXCLUDED_TICKET_STATUSES.includes(i.status.name.toLowerCase()))
        setJqlSelectedKeys(new Set(activeJqlIssues.map(i => i.key)))
      }
    } catch {
      setJqlError('Failed to execute JQL query')
    } finally {
      setJqlLoading(false)
    }
  }

  const handleJqlLoadMore = async () => {
    setJqlLoading(true)
    try {
      const result = await searchJiraByJql(jqlQuery.trim(), jqlResults.length)
      if (!result.error) {
        setJqlResults(prev => [...prev, ...result.issues])
        setJqlHasMore(result.hasMore)
        // Auto-select newly loaded items
        setJqlSelectedKeys(prev => {
          const next = new Set(prev)
          result.issues.forEach(i => next.add(i.key))
          return next
        })
      }
    } finally {
      setJqlLoading(false)
    }
  }

  const toggleJqlKey = (key: string) => {
    setJqlSelectedKeys(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const toggleJqlSelectAll = () => {
    if (jqlSelectedKeys.size === jqlResults.length) {
      setJqlSelectedKeys(new Set())
    } else {
      setJqlSelectedKeys(new Set(jqlResults.map(i => i.key)))
    }
  }

  const handleJqlImport = () => {
    const newTickets = [...new Set([...jiraTickets, ...jqlSelectedKeys])]
    setJiraTickets(newTickets)
    setJqlResults([])
    setJqlQuery('')
    setJqlExpanded(false)
  }

  // Auto-run handler
  const handleAutoRunToggle = async () => {
    if (!currentProfile) return
    setAutoRunSaving(true)
    try {
      if (autoRunConfig?.enabled) {
        // Disable
        const { autoRun } = await updateAutoRun(currentProfile.profileId, { enabled: false })
        setAutoRunConfig(autoRun)
      } else {
        // Need to select a board first if not already configured
        if (autoRunConfig?.boardId) {
          // Re-enable with existing board
          const { autoRun } = await updateAutoRun(currentProfile.profileId, {
            enabled: true,
            boardId: autoRunConfig.boardId,
            boardName: autoRunConfig.boardName,
          })
          setAutoRunConfig(autoRun)
        } else {
          // Fetch boards and pick the first one
          const { boards } = await getJiraBoards()
          if (boards.length > 0) {
            const { autoRun } = await updateAutoRun(currentProfile.profileId, {
              enabled: true,
              boardId: boards[0].id,
              boardName: boards[0].name,
            })
            setAutoRunConfig(autoRun)
          }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? errorMessage(err) : 'Failed to update auto-run')
    } finally {
      setAutoRunSaving(false)
    }
  }


  // ============================================================================
  // Render: Initializing
  // ============================================================================
  if (initializing) {
    return (
      <div className="min-h-screen bg-linear-bg flex items-center justify-center">
        <div className="text-center">
          <div className="spinner spinner-lg mx-auto mb-4"></div>
          <div className="text-linear-text-muted">Loading...</div>
        </div>
      </div>
    )
  }

  // ============================================================================
  // Render: Instructions
  // ============================================================================
  if (currentScreen === 'instructions') {
    return (
      <Suspense fallback={
        <div className="min-h-screen bg-linear-bg flex items-center justify-center">
          <div className="text-center">
            <div className="spinner spinner-lg mx-auto mb-4"></div>
            <div className="text-linear-text-muted">Loading instructions...</div>
          </div>
        </div>
      }>
        <RiskPrioritizationInstructions
          onBack={() => {
            navigate('/risk-prioritization/')
            setCurrentScreen('profiles')
          }}
        />
      </Suspense>
    )
  }

  // ============================================================================
  // Render: Profile List
  // ============================================================================
  if (currentScreen === 'profiles') {
    return (
      <AppLayout
        breadcrumbs={[
          { label: 'Risk Prioritization' },
        ]}
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-linear-text mb-2">Risk Prioritization</h1>
            <p className="text-linear-text-muted">
              AI-powered test prioritization based on sprint changes and risk analysis
            </p>
          </div>

          {error && (
            <div className="toast toast-error mb-4">
              <svg className="w-5 h-5 text-error flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="text-linear-text">{error}</span>
            </div>
          )}

          {/* Create New Profile */}
          <div className="card mb-6">
            <h2 className="text-lg font-semibold text-linear-text mb-4">Create New Profile</h2>
            <div className="flex gap-4">
              <input
                type="text"
                value={newProfileName}
                onChange={(e) => setNewProfileName(e.target.value)}
                placeholder="Profile name"
                className="input flex-1"
                onKeyDown={(e) => e.key === 'Enter' && handleCreateProfile()}
              />
              <button
                onClick={handleCreateProfile}
                disabled={loading || !newProfileName.trim()}
                className="btn btn-primary"
              >
                Create
              </button>
            </div>
          </div>

          {/* Profile List */}
          <div className="card">
            <h2 className="text-lg font-semibold text-linear-text mb-4">Saved Profiles</h2>
            {profiles.length === 0 && (
              <div className="text-linear-text-muted py-8 text-center">
                No profiles yet. Create one to get started.
              </div>
            )}
            <div className="space-y-3">
              {profiles.map((profile) => (
                <div
                  key={profile.profileId}
                  onClick={() => handleSelectProfile(profile.profileId)}
                  className="bg-linear-bg-card border border-linear-border rounded-lg p-4 cursor-pointer transition-all hover:bg-linear-bg-hover hover:border-linear-accent"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-linear-text">{profile.name}</h3>
                      <p className="text-sm text-linear-text-muted">
                        {profile.testCaseCount} test cases - {profile.areaCount} areas
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {profile.testCaseCount === 0 && (
                        <button
                          onClick={async (e) => {
                            e.stopPropagation()
                            try {
                              const fullProfile = await getProfile(profile.profileId)
                              setCurrentProfile(fullProfile)
                              setCurrentScreen('setup')
                              setSetupStep('upload')
                              navigate(`/risk-prioritization/${profile.profileId}/setup`)
                            } catch (err) {
                              setError(friendlyError(errorMessage(err) || 'Failed to load profile', 'load this profile'))
                            }
                          }}
                          className="btn btn-secondary text-warning"
                        >
                          Complete Setup
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setDeleteModalProfileId(profile.profileId)
                        }}
                        className="btn btn-ghost text-linear-text-muted hover:text-error"
                        title="Delete profile"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Delete Profile Confirmation Modal */}
        {deleteModalProfileId && (() => {
          const profileToDelete = profiles.find((p) => p.profileId === deleteModalProfileId)
          return (
            <div className="modal-overlay" onClick={() => setDeleteModalProfileId(null)}>
              <div className="modal max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                {/* Modal Header */}
                <div className="modal-header">
                  <div className="flex items-center space-x-2">
                    <svg className="w-6 h-6 text-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <h3 className="text-lg font-semibold text-linear-text">Delete Profile</h3>
                  </div>
                  <button
                    onClick={() => setDeleteModalProfileId(null)}
                    className="text-linear-text-muted hover:text-linear-text"
                    disabled={isDeleting}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Modal Body */}
                <div className="modal-body">
                  <p className="text-linear-text-secondary mb-2">
                    Are you sure you want to delete <strong className="text-linear-text">{profileToDelete?.name}</strong>? This action cannot be undone.
                  </p>
                  <p className="text-sm text-linear-text-muted">
                    This will permanently delete the profile and all associated test library data ({profileToDelete?.testCaseCount || 0} test cases).
                  </p>
                </div>

                {/* Modal Footer */}
                <div className="modal-footer bg-linear-bg-hover">
                  <button
                    onClick={() => setDeleteModalProfileId(null)}
                    className="btn btn-outline"
                    disabled={isDeleting}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDeleteProfile(deleteModalProfileId)}
                    className="btn btn-danger"
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <>
                        <div className="spinner spinner-sm"></div>
                        <span>Deleting...</span>
                      </>
                    ) : (
                      'Delete Profile'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )
        })()}
      </AppLayout>
    )
  }

  // ============================================================================
  // Render: Setup Wizard
  // ============================================================================
  if (currentScreen === 'setup') {
    // Step indicators
    const steps = [
      { key: 'upload', label: 'Upload', number: 1 },
      { key: 'context', label: 'Context', number: 2 },
      { key: 'weights', label: 'Weights', number: 3 },
      { key: 'review', label: 'Review', number: 4 },
    ]
    const currentStepIndex = steps.findIndex((s) => s.key === setupStep)

    return (
      <AppLayout
        breadcrumbs={[
          { label: 'Risk Prioritization', href: '/risk-prioritization/' },
          ...(currentProfile && currentProfile.testLibrary.totalTestCases > 0
            ? [{ label: currentProfile.name, href: `/risk-prioritization/${currentProfile.profileId}` }]
            : [{ label: currentProfile?.name || 'New Profile' }]
          ),
          { label: 'Setup' },
        ]}
      >
        <div className="max-w-[1400px] mx-auto">
          {/* Step Progress Indicator */}
          <div className="mb-8">
            <div className="flex items-center">
              {steps.map((step, index) => (
                <Fragment key={step.key}>
                  {/* Step circle and label - always centered together */}
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                        index <= currentStepIndex
                          ? 'bg-linear-accent text-white'
                          : 'bg-linear-bg-hover text-linear-text-muted'
                      }`}
                    >
                      {index < currentStepIndex ? (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        step.number
                      )}
                    </div>
                    <span className={`text-xs mt-2 whitespace-nowrap ${index <= currentStepIndex ? 'text-linear-text' : 'text-linear-text-muted'}`}>
                      {step.label}
                    </span>
                  </div>
                  {/* Connecting line between steps */}
                  {index < steps.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 mx-2 self-start mt-5 ${
                        index < currentStepIndex ? 'bg-linear-accent' : 'bg-linear-border'
                      }`}
                    />
                  )}
                </Fragment>
              ))}
            </div>
          </div>

          {error && (
            <div className="toast toast-error mb-4">
              <svg className="w-5 h-5 text-error flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="text-linear-text">{error}</span>
            </div>
          )}

          {warning && (
            <div className="toast toast-warning mb-4">
              <svg className="w-5 h-5 text-warning flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-linear-text">{warning}</span>
            </div>
          )}

          {/* Step 1: Upload CSV */}
          {setupStep === 'upload' && (
            <div className="card">
              <h2 className="text-xl font-semibold text-linear-text mb-2">Upload Test Library CSV</h2>
              <p className="text-linear-text-muted mb-6">
                Upload your test case library from TestRail or Zephyr Scale.
              </p>

              {/* Upload Mode Selector */}
              {currentProfile && currentProfile.testLibrary.totalTestCases > 0 && (
                <div className="mb-6 p-4 bg-linear-bg-hover border border-linear-border rounded-lg">
                  <div className="text-sm font-medium text-linear-text mb-3">Upload Mode</div>
                  <div className="space-y-3">
                    <label className="flex items-start cursor-pointer group">
                      <input
                        type="radio"
                        name="uploadMode"
                        value="replace"
                        checked={uploadMode === 'replace'}
                        onChange={(e) => setUploadMode(e.target.value as 'replace')}
                        className="mt-1 mr-3 accent-linear-accent"
                      />
                      <div>
                        <div className="font-medium text-linear-text group-hover:text-linear-accent transition-colors">Replace All Test Cases</div>
                        <div className="text-sm text-linear-text-muted">
                          Delete existing {currentProfile.testLibrary.totalTestCases} test cases and replace with new CSV
                        </div>
                      </div>
                    </label>
                    <label className="flex items-start cursor-pointer group">
                      <input
                        type="radio"
                        name="uploadMode"
                        value="append"
                        checked={uploadMode === 'append'}
                        onChange={(e) => setUploadMode(e.target.value as 'append')}
                        className="mt-1 mr-3 accent-linear-accent"
                      />
                      <div>
                        <div className="font-medium text-linear-text group-hover:text-linear-accent transition-colors">Append Test Cases</div>
                        <div className="text-sm text-linear-text-muted">
                          Add new test cases to existing {currentProfile.testLibrary.totalTestCases}. Duplicates (same ID) will be skipped.
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* File Upload Zone */}
              <div
                className={`upload-zone mb-6 ${dragActive ? 'drag-active' : ''}`}
                onDragOver={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setDragActive(true)
                }}
                onDragEnter={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setDragActive(true)
                }}
                onDragLeave={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    setDragActive(false)
                  }
                }}
                onDrop={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setDragActive(false)
                  const file = e.dataTransfer.files?.[0]
                  if (file && file.name.toLowerCase().endsWith('.csv')) {
                    setCsvFile(file)
                    if (!currentProfile || currentProfile.testLibrary.totalTestCases === 0) {
                      setUploadMode('replace')
                    }
                  }
                }}
              >
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => {
                    setCsvFile(e.target.files?.[0] || null)
                    if (!currentProfile || currentProfile.testLibrary.totalTestCases === 0) {
                      setUploadMode('replace')
                    }
                  }}
                  className="hidden"
                  id="csv-upload"
                />
                <label htmlFor="csv-upload" className="cursor-pointer block">
                  <svg className="w-12 h-12 text-linear-text-muted mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  {csvFile ? (
                    <div className="text-linear-text font-medium">{csvFile.name}</div>
                  ) : (
                    <>
                      <div className="text-linear-text font-medium mb-1">Click or drag to upload CSV file</div>
                      <div className="text-linear-text-muted text-sm">Supports TestRail and Zephyr Scale formats</div>
                    </>
                  )}
                </label>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={handleUploadCSV}
                  disabled={loading || !csvFile}
                  className="btn btn-primary"
                >
                  {loading ? (
                    <>
                      <div className="spinner spinner-sm"></div>
                      Uploading...
                    </>
                  ) : (
                    uploadMode === 'append' ? 'Append Test Cases' : 'Upload'
                  )}
                </button>
                {currentProfile && currentProfile.testLibrary.totalTestCases > 0 && (
                  <button
                    onClick={() => setSetupStep('context')}
                    disabled={loading}
                    className="btn btn-outline"
                  >
                    Skip — Keep Existing Tests
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Area Contexts */}
          {setupStep === 'context' && currentProfile && (
            <div className="card">
              <h2 className="text-xl font-semibold text-linear-text mb-2">Area Context</h2>
              <p className="text-linear-text-muted mb-6">
                Provide context for each area (historical bugs, criticality, etc.)
              </p>
              <div className="space-y-4 mb-6">
                {currentProfile.testLibrary.areas.map((area) => (
                  <div key={area.areaId} className="bg-linear-bg-hover border border-linear-border rounded-lg p-4">
                    <h3 className="font-semibold text-linear-text mb-3">
                      {area.name} <span className="text-linear-text-muted font-normal">({area.testCaseCount} tests)</span>
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="label">Historical Bugs</label>
                        <textarea
                          value={currentProfile.areaContexts[area.areaId]?.historicalBugs || ''}
                          onChange={(e) =>
                            setCurrentProfile({
                              ...currentProfile,
                              areaContexts: {
                                ...currentProfile.areaContexts,
                                [area.areaId]: {
                                  ...currentProfile.areaContexts[area.areaId],
                                  historicalBugs: e.target.value,
                                },
                              },
                            })
                          }
                          className="textarea w-full"
                          rows={2}
                          placeholder="e.g. 15 bugs last quarter, critical auth issues"
                        />
                      </div>
                      <div>
                        <label className="label">Business Critical</label>
                        <textarea
                          value={currentProfile.areaContexts[area.areaId]?.businessCritical || ''}
                          onChange={(e) =>
                            setCurrentProfile({
                              ...currentProfile,
                              areaContexts: {
                                ...currentProfile.areaContexts,
                                [area.areaId]: {
                                  ...currentProfile.areaContexts[area.areaId],
                                  businessCritical: e.target.value,
                                },
                              },
                            })
                          }
                          className="textarea w-full"
                          rows={2}
                          placeholder="e.g. High - payment processing, revenue impact"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setSetupStep('upload')}
                  className="btn btn-outline"
                >
                  Back
                </button>
                <button
                  onClick={handleSaveContexts}
                  disabled={loading}
                  className="btn btn-primary"
                >
                  {loading ? (
                    <>
                      <div className="spinner spinner-sm"></div>
                      Saving...
                    </>
                  ) : (
                    'Next'
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Risk Weights */}
          {setupStep === 'weights' && currentProfile && (
            <div className="card">
              <h2 className="text-xl font-semibold text-linear-text mb-2">Risk Weights</h2>
              <p className="text-linear-text-muted mb-6">
                Adjust weights (must sum to 100). Current sum:{' '}
                <span className="text-linear-text font-medium">
                  {formatPercent(
                    Object.values(currentProfile.riskWeights).reduce((a, b) => a + b, 0),
                  )}
                </span>
              </p>
              <div className="space-y-6 mb-6">
                {(Object.keys(currentProfile.riskWeights) as Array<keyof typeof currentProfile.riskWeights>).map((key) => (
                  <div key={key} className="bg-linear-bg-hover border border-linear-border rounded-lg p-4">
                    <label className="label capitalize mb-3">
                      {key.replace(/([A-Z])/g, ' $1').trim()}: <span className="text-linear-accent">{formatPercent(currentProfile.riskWeights[key])}</span>
                    </label>
                    <div className="flex gap-3 items-center">
                      <button
                        onClick={() => adjustWeight(key, -5)}
                        className="btn btn-secondary px-3 py-1 text-sm"
                      >
                        -5
                      </button>
                      <div className="flex-1 relative">
                        <div className="progress-bar h-2">
                          <div
                            className="progress-bar-fill"
                            style={{ width: `${currentProfile.riskWeights[key]}%` }}
                          />
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={currentProfile.riskWeights[key]}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value)
                            adjustWeight(key, val - currentProfile.riskWeights[key])
                          }}
                          className="absolute inset-0 w-full opacity-0 cursor-pointer"
                        />
                      </div>
                      <button
                        onClick={() => adjustWeight(key, 5)}
                        className="btn btn-secondary px-3 py-1 text-sm"
                      >
                        +5
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setSetupStep('context')}
                  className="btn btn-outline"
                >
                  Back
                </button>
                <button
                  onClick={handleSaveWeights}
                  disabled={loading}
                  className="btn btn-primary"
                >
                  {loading ? (
                    <>
                      <div className="spinner spinner-sm"></div>
                      Saving...
                    </>
                  ) : (
                    'Next'
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {setupStep === 'review' && currentProfile && (
            <div className="card">
              <h2 className="text-xl font-semibold text-linear-text mb-6">Review Configuration</h2>
              <div className="space-y-6 mb-6">
                <div className="bg-linear-bg-hover border border-linear-border rounded-lg p-4">
                  <h3 className="font-semibold text-linear-text mb-2 flex items-center gap-2">
                    <svg className="w-5 h-5 text-linear-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Test Library
                  </h3>
                  <p className="text-linear-text-secondary">
                    <span className="text-linear-text font-medium">{currentProfile.testLibrary.totalTestCases}</span> test cases across{' '}
                    <span className="text-linear-text font-medium">{currentProfile.testLibrary.areas.length}</span> areas
                  </p>
                </div>
                <div className="bg-linear-bg-hover border border-linear-border rounded-lg p-4">
                  <h3 className="font-semibold text-linear-text mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-linear-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                    </svg>
                    Risk Weights
                  </h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {(Object.keys(currentProfile.riskWeights) as Array<keyof typeof currentProfile.riskWeights>).map(
                      (key) => (
                        <div key={key} className="flex justify-between text-linear-text-secondary">
                          <span className="capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                          <span className="text-linear-text font-medium">{formatPercent(currentProfile.riskWeights[key])}</span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setSetupStep('weights')}
                  className="btn btn-outline"
                >
                  Back
                </button>
                <button
                  onClick={handleCompleteSetup}
                  className="btn btn-primary"
                >
                  Complete Setup
                </button>
              </div>
            </div>
          )}
        </div>
      </AppLayout>
    )
  }

  // ============================================================================
  // Render: Sprint Analysis
  // ============================================================================
  if (currentScreen === 'analysis') {
    return (
      <AppLayout
        breadcrumbs={[
          { label: 'Risk Prioritization', href: '/risk-prioritization/' },
          { label: currentProfile?.name || 'Analysis' },
        ]}
        headerActions={
          <button
            onClick={() => {
              if (currentProfile) {
                setCurrentScreen('setup')
                setSetupStep('upload')
                navigate(`/risk-prioritization/${currentProfile.profileId}/setup`)
              }
            }}
            className="btn btn-secondary text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Edit Setup
          </button>
        }
      >
        <div className="max-w-[1400px] mx-auto">
          {error && (
            <div className="toast toast-error mb-4">
              <svg className="w-5 h-5 text-error flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="text-linear-text">{error}</span>
            </div>
          )}

          <div className="card">
            {/* Header row with title and Sprint Import button */}
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xl font-semibold text-linear-text">Enter JIRA Tickets</h2>
              {jiraStatus?.status === 'active' && (
                <div className="flex items-center gap-2">
                  {jiraTickets.length >= 2 && (
                    <button
                      onClick={() => setJiraTickets([])}
                      disabled={analysisStatus === 'processing'}
                      className="btn btn-ghost text-sm text-linear-text-muted"
                      title="Clear all tickets"
                    >
                      Clear All
                    </button>
                  )}
                  <button
                    onClick={() => setSprintModalOpen(true)}
                    disabled={analysisStatus === 'processing'}
                    className="btn btn-outline text-sm"
                  >
                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Import from Sprint
                  </button>
                </div>
              )}
            </div>
            <p className="text-linear-text-muted mb-6">
              Type ticket IDs and press Space, Comma, or Enter to add (e.g. BAT-100)
            </p>

            {/* Pills/Tags Container */}
            <div className="bg-linear-bg-input border border-linear-border-input rounded-lg p-3 mb-6 focus-within:border-linear-accent-focus transition-colors">
              <div className="flex flex-wrap items-center gap-2">
                {jiraTickets.map((ticket) => (
                  <div
                    key={ticket}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-linear-accent/20 text-linear-accent rounded-full text-sm font-medium flex-shrink-0"
                  >
                    <span>{ticket}</span>
                    <button
                      onClick={() => removeJiraTicket(ticket)}
                      disabled={analysisStatus === 'processing'}
                      className="hover:bg-linear-accent/30 rounded-full p-0.5 transition-colors"
                      type="button"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
                <input
                  type="text"
                  value={currentTicketInput}
                  onChange={(e) => setCurrentTicketInput(e.target.value)}
                  onKeyDown={handleTicketInputKeyDown}
                  className="flex-1 min-w-[200px] bg-transparent border-none outline-none text-linear-text placeholder-linear-text-placeholder"
                  placeholder={jiraTickets.length === 0 ? "Type BAT-100 and press Space or Enter..." : ""}
                  disabled={analysisStatus === 'processing'}
                />
              </div>
            </div>

            {/* JQL Import Section */}
            {jiraStatus?.status === 'active' && (
              <div className="mt-4 mb-6">
                <button
                  onClick={() => setJqlExpanded(!jqlExpanded)}
                  className="flex items-center gap-2 text-sm text-linear-text-muted hover:text-linear-text transition-colors"
                >
                  <svg className={`w-4 h-4 transition-transform ${jqlExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  Advanced: Import from JQL
                </button>

                {jqlExpanded && (
                  <div className="mt-3 p-4 bg-linear-bg-input border border-linear-border rounded-lg">
                    <textarea
                      value={jqlQuery}
                      onChange={e => setJqlQuery(e.target.value)}
                      placeholder='project = BAT AND fixVersion = "3.2"'
                      className="textarea font-mono text-sm w-full"
                      rows={3}
                    />
                    <div className="flex items-center gap-3 mt-3">
                      <button
                        onClick={handleJqlPreview}
                        disabled={!jqlQuery.trim() || jqlLoading}
                        className="btn btn-outline text-sm"
                      >
                        {jqlLoading ? 'Searching...' : 'Preview'}
                      </button>
                      {jqlError && <span className="text-sm text-error">{jqlError}</span>}
                      {jqlTotal > 0 && !jqlError && <span className="text-sm text-linear-text-muted">{jqlTotal} ticket{jqlTotal !== 1 ? 's' : ''} found</span>}
                    </div>

                    {/* JQL Results list with checkboxes */}
                    {jqlResults.length > 0 && (
                      <div className="mt-3 max-h-60 overflow-y-auto space-y-1">
                        <div className="flex items-center justify-between mb-2">
                          <label className="flex items-center gap-2 text-sm text-linear-text-muted cursor-pointer">
                            <input
                              type="checkbox"
                              checked={jqlSelectedKeys.size === jqlResults.length && jqlResults.length > 0}
                              onChange={toggleJqlSelectAll}
                              className="accent-linear-accent"
                            />
                            Select All
                          </label>
                          <button
                            onClick={handleJqlImport}
                            disabled={jqlSelectedKeys.size === 0}
                            className="btn btn-primary text-sm"
                          >
                            Import {jqlSelectedKeys.size} Ticket{jqlSelectedKeys.size !== 1 ? 's' : ''}
                          </button>
                        </div>
                        {jqlResults.map(issue => {
                          const isExcluded = EXCLUDED_TICKET_STATUSES.includes(issue.status.name.toLowerCase())
                          return (
                          <label key={issue.key} className={`flex items-center gap-3 p-2 rounded hover:bg-linear-bg-hover cursor-pointer ${isExcluded ? 'opacity-50' : ''}`}>
                            <input
                              type="checkbox"
                              checked={jqlSelectedKeys.has(issue.key)}
                              onChange={() => toggleJqlKey(issue.key)}
                              className="accent-linear-accent flex-shrink-0"
                            />
                            <span className={`font-mono text-sm flex-shrink-0 ${isExcluded ? 'text-linear-text-muted line-through' : 'text-linear-accent'}`}>{issue.key}</span>
                            <span className={`text-sm truncate ${isExcluded ? 'text-linear-text-muted line-through' : 'text-linear-text'}`}>{issue.summary}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${statusColor(issue.status.color)}`}>{issue.status.name}</span>
                          </label>
                          )
                        })}
                        {jqlHasMore && (
                          <button
                            onClick={handleJqlLoadMore}
                            disabled={jqlLoading}
                            className="w-full text-center text-sm text-linear-accent hover:underline py-2"
                          >
                            {jqlLoading ? 'Loading...' : 'Load More'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Auto-Run Section */}
            {jiraStatus?.status === 'active' && currentProfile && (() => {
              const noTestLibrary = !currentProfile.testLibrary || currentProfile.testLibrary.totalTestCases === 0
              const isAnalyzing = analysisStatus === 'processing'
              const isDisabled = autoRunSaving || noTestLibrary || isAnalyzing || noJiraBoards
              const tooltipText = noTestLibrary
                ? 'Upload a test library first. Auto-run needs test areas to analyze.'
                : isAnalyzing
                  ? 'Analysis in progress. Change auto-run settings after it finishes.'
                  : noJiraBoards
                    ? 'No JIRA boards found. Create a board in JIRA to enable auto-run.'
                    : autoRunConfig?.enabled
                      ? `Auto-run is on. Monitoring ${autoRunConfig.boardName || 'your board'} for new sprints.`
                      : 'Automatically trigger analysis when a new sprint starts in JIRA.'

              return (
              <div className="mt-4 mb-6 p-4 border border-linear-border rounded-lg bg-linear-bg-input/50">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-linear-text">Auto-run on Sprint Start</h3>
                    <p className="text-xs text-linear-text-muted mt-0.5">
                      {autoRunConfig?.enabled
                        ? `Monitoring ${autoRunConfig.boardName || 'board'} for new sprints`
                        : 'Automatically run analysis when a new sprint starts'}
                    </p>
                  </div>
                  <div
                    className="relative"
                    onMouseEnter={() => { if (isDisabled && !autoRunSaving) setAutoRunTooltipVisible(true) }}
                    onMouseLeave={() => setAutoRunTooltipVisible(false)}
                  >
                    <button
                      onClick={() => { if (!isDisabled) handleAutoRunToggle() }}
                      aria-disabled={isDisabled}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        isDisabled && !autoRunSaving
                          ? 'bg-gray-700 opacity-40 cursor-not-allowed'
                          : autoRunConfig?.enabled ? 'bg-success' : 'bg-linear-text-muted'
                      }`}
                      aria-label="Toggle auto-run"
                    >
                      <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                        autoRunConfig?.enabled ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                    {autoRunTooltipVisible && !autoRunSaving && (
                      <div className="absolute right-0 bottom-full mb-2 w-64 p-2.5 bg-gray-900 text-gray-200 text-xs rounded-lg shadow-lg pointer-events-none z-50">
                        {tooltipText}
                        <div className="absolute top-full right-3 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-gray-900" />
                      </div>
                    )}
                  </div>
                </div>
                {autoRunConfig?.lastAutoRunAt && (
                  <p className="text-xs text-linear-text-muted mt-2">
                    Last run: {new Date(autoRunConfig.lastAutoRunAt).toLocaleDateString()}
                    {autoRunConfig.lastSprintName && ` \u2014 ${autoRunConfig.lastSprintName}`}
                  </p>
                )}
                {autoRunConfig?.lastAutoRunError && (
                  <p className="text-xs text-error mt-1">Error: {autoRunConfig.lastAutoRunError}</p>
                )}
              </div>
              )
            })()}

            <button
              onClick={handleStartAnalysis}
              disabled={loading || analysisStatus === 'processing' || jiraTickets.length === 0}
              className="btn btn-primary"
            >
              {analysisStatus === 'processing' ? (
                <>
                  <div className="spinner spinner-sm"></div>
                  Analyzing {jiraTickets.length} ticket{jiraTickets.length !== 1 ? 's' : ''}...
                </>
              ) : (
                'Analyze'
              )}
            </button>
          </div>
        </div>

        {sprintModalOpen && (
          <SprintImportModal
            onClose={() => setSprintModalOpen(false)}
            onImport={(keys) => {
              const newTickets = [...new Set([...jiraTickets, ...keys])]
              setJiraTickets(newTickets)
              setSprintModalOpen(false)
            }}
            appMode="risk-prioritization"
            jiraStatus={jiraStatus}
          />
        )}
      </AppLayout>
    )
  }

  // ============================================================================
  // Render: Results
  // ============================================================================
  if (currentScreen === 'results' && analysisResults) {
    const { rankedAreas, summary } = analysisResults

    return (
      <AppLayout
        breadcrumbs={[
          { label: 'Risk Prioritization', href: '/risk-prioritization/' },
          { label: currentProfile?.name || 'Profile', href: currentProfile ? `/risk-prioritization/${currentProfile.profileId}` : undefined },
          { label: 'Results' },
        ]}
        headerActions={
          <button
            onClick={() => {
              if (currentProfile) {
                setCurrentScreen('analysis')
                setJiraTickets([])
                setCurrentTicketInput('')
                setAnalysisStatus('idle')
                navigate(`/risk-prioritization/${currentProfile.profileId}`)
              }
            }}
            className="btn btn-primary text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Analysis
          </button>
        }
      >
        <div className="max-w-[1400px] mx-auto">
          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="card p-4">
              <div className="text-2xl font-bold text-linear-text">{summary.totalAreas}</div>
              <div className="text-sm text-linear-text-muted">Total Areas</div>
            </div>
            <div className="card p-4">
              <div className="text-2xl font-bold text-error">{summary.highRiskAreas}</div>
              <div className="text-sm text-linear-text-muted">High Risk</div>
            </div>
            <div className="card p-4">
              <div className="text-2xl font-bold text-warning">{summary.mediumRiskAreas}</div>
              <div className="text-sm text-linear-text-muted">Medium Risk</div>
            </div>
            <div className="card p-4">
              <div className="text-2xl font-bold text-success">{summary.lowRiskAreas}</div>
              <div className="text-sm text-linear-text-muted">Low Risk</div>
            </div>
          </div>

          {/* Applied Weights */}
          {analysisResults.weightsUsed && (
            <div className="card p-4 mb-6">
              <div className="text-xs font-medium text-linear-text-muted uppercase tracking-wider mb-3">Applied Weights</div>
              <div className="grid grid-cols-5 gap-3">
                {([
                  ['Change Proximity', analysisResults.weightsUsed.changeProximity],
                  ['Historical Bugs', analysisResults.weightsUsed.historicalBugs],
                  ['Business Critical', analysisResults.weightsUsed.businessCritical],
                  ['Dependencies', analysisResults.weightsUsed.dependencies],
                  ['Last Tested', analysisResults.weightsUsed.lastTested],
                ] as [string, number][]).map(([label, value]) => (
                  <div key={label} className="text-center">
                    <div className="text-lg font-bold text-linear-accent">{formatPercent(value)}</div>
                    <div className="text-xs text-linear-text-muted">{label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Export Dropdown */}
          <div className="relative mb-6">
            <button
              onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
              className="btn btn-outline text-sm flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export Results
              <svg className={`w-4 h-4 transition-transform ${exportDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {exportDropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setExportDropdownOpen(false)}
                />
                <div className="dropdown absolute left-0 mt-2 w-48 z-20">
                  <button
                    onClick={() => {
                      handleExport('csv')
                      setExportDropdownOpen(false)
                    }}
                    className="dropdown-item w-full text-left"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export as CSV
                  </button>
                  <button
                    onClick={() => {
                      handleExport('json')
                      setExportDropdownOpen(false)
                    }}
                    className="dropdown-item w-full text-left"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                    </svg>
                    Export as JSON
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Ranked Areas */}
          <div className="space-y-4">
            {rankedAreas.map((area) => (
              <div key={area.areaId} className="card overflow-hidden">
                <div
                  className="p-4 cursor-pointer hover:bg-linear-bg-hover transition-colors"
                  onClick={() => toggleArea(area.areaId)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <span
                          className={`badge font-bold ${
                            area.riskLevel === 'high'
                              ? 'badge-high'
                              : area.riskLevel === 'medium'
                              ? 'badge-medium'
                              : 'badge-low'
                          }`}
                        >
                          {area.riskScore.toFixed(0)}
                        </span>
                        <h3 className="text-lg font-semibold text-linear-text">{area.areaName}</h3>
                      </div>
                      <p
                        className="text-linear-text-secondary mt-2"
                        title="The AI's explanation for this area's risk score, usually referencing the specific tickets that drove it."
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="font-semibold text-linear-text mr-1">Why this score:</span>
                        {linkifyJiraKeys(area.reasoning, jiraBaseUrl)}
                      </p>
                      <div
                        className="mt-2 text-sm text-linear-text-muted"
                        title="The AI reads each ticket's summary and description from JIRA and links tickets to the section they actually affect. Connect JIRA for the richest matches."
                      >
                        {area.testCases.length} test cases -
                        {area.matchedTickets.length > 0 ? (
                          <>
                            {' '}
                            Tickets:{' '}
                            {linkifyJiraKeys(area.matchedTickets.join(', '), jiraBaseUrl)}
                          </>
                        ) : (
                          ' No matched tickets'
                        )}
                      </div>
                    </div>
                    <svg
                      className={`w-5 h-5 text-linear-text-muted transition-transform ${
                        expandedAreas.has(area.areaId) ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {expandedAreas.has(area.areaId) && (
                  <div className="border-t border-linear-border p-4 bg-linear-bg-hover">
                    <h4 className="font-semibold text-linear-text mb-3">Factor Scores</h4>
                    <div className="grid grid-cols-5 gap-3 mb-6">
                      {([
                        ['Change Proximity', area.factorScores.changeProximity, analysisResults.weightsUsed?.changeProximity],
                        ['Historical Bugs', area.factorScores.historicalBugs, analysisResults.weightsUsed?.historicalBugs],
                        ['Business Critical', area.factorScores.businessCritical, analysisResults.weightsUsed?.businessCritical],
                        ['Dependencies', area.factorScores.dependencies, analysisResults.weightsUsed?.dependencies],
                        ['Last Tested', area.factorScores.lastTested, analysisResults.weightsUsed?.lastTested],
                      ] as [string, number, number | undefined][]).map(([label, score, weight]) => (
                        <div key={label} className="bg-linear-bg-card border border-linear-border rounded-lg p-3 text-center">
                          <div className="text-xs text-linear-text-muted mb-1">{label}</div>
                          <div className="font-bold text-linear-text">{score.toFixed(0)}</div>
                          {weight !== undefined && (
                            <div
                              className="text-xs text-linear-text-muted mt-1"
                              title="Weight applied to this factor in the final risk score."
                            >
                              Weight: {formatPercent(weight)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    <h4 className="font-semibold text-linear-text mb-3">Test Cases ({area.testCases.length})</h4>
                    {(() => {
                      const isExpanded = expandedAreaTestCases.has(area.areaId)
                      const hiddenCount = area.testCases.length - 10
                      const visibleTestCases = isExpanded || hiddenCount <= 0
                        ? area.testCases
                        : area.testCases.slice(0, 10)
                      const listId = `area-test-cases-${area.areaId}`
                      return (
                        <div className="space-y-2 text-sm max-h-64 overflow-y-auto">
                          <div id={listId}>
                            {visibleTestCases.map((tc) => (
                              <div key={tc.id} className="text-linear-text-secondary py-1 border-b border-linear-border-muted last:border-0">
                                <span className="text-linear-text-muted">{tc.id}:</span> {tc.title}
                              </div>
                            ))}
                          </div>
                          {hiddenCount > 0 && (
                            <button
                              type="button"
                              onClick={() => toggleAreaTestCases(area.areaId)}
                              className="text-linear-accent hover:underline italic py-1 text-left w-full focus:outline-none focus:ring-2 focus:ring-linear-accent rounded"
                              aria-expanded={isExpanded}
                              aria-controls={listId}
                              aria-label={isExpanded ? 'Show fewer test cases' : `Show ${hiddenCount} more test cases`}
                            >
                              {isExpanded ? 'Show fewer' : `...and ${hiddenCount} more`}
                            </button>
                          )}
                        </div>
                      )
                    })()}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </AppLayout>
    )
  }

  return null
}
